import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { useNavigate } from "react-router";
import { useTelephony } from "../../context/TelephonyContext";
import { I } from "../redesign/kvpIcons";
import { useElapsed, fmtMMSS } from "./callTimer";
import { anrufAnzeigeName, anrufAnzeigeNummer, PersonIcon } from "./telephonyUtils";
import KollegenWeiterleitung from "./KollegenWeiterleitung";
import { TelefonGeraeteWahl } from "./TelefonGeraeteWahl";
import { darfNotizFokusNehmen } from "./anrufNotiz";
import { softphone, useSoftphone } from "../../services/linkusSdk";
import { KEIN_TON_NACH_MS, keinTonNachAnnahme } from "../../services/softphoneKeinTon";
// Das Widget wird global (über dem Provider) gemountet und kann auf JEDER Seite
// erscheinen — auch auf solchen, die `contactRedesign.css` nicht selbst laden.
// Darum die kvp-rd-Styles hier explizit importieren, damit Optik UND Positionierung
// (`.callw-root`) immer greifen (sonst floss die Karte unstyled ins Layout = SP7-Bug
// „Popup erscheint oben rechts"). Die effektive Position setzen wir zusätzlich als
// Inline-Style am Root (höchste Spezifität, immun gegen Cascade-/Containing-Block-
// Überraschungen).
import "../redesign/contactRedesign.css";
import { alleFenster, aktivesDokument } from "../windowManager/browserFenster/fensterRegister";

/**
 * CallWidget — EIN vereinheitlichtes, blur-freies, minimierbares Aktiv-Call-Widget
 * (SP2, ersetzt SoftphoneWidget + altes SSE-CallWidget; ADR 0001).
 *
 * Schwebende Karte, portaliert nach document.body. Die Wurzel trägt `kvp-rd`,
 * damit die Design-Tokens greifen. Position (Inline-Style am Root) in ALLEN
 * Zuständen — ringing (eingehend klingelt), dialing (ausgehend) und active:
 *  - ZENTRIERT (Viewport-Mitte). Früher stand die klingelnde Karte unten rechts
 *    (SP7/P5b) und wurde dort vom Linkus-Desktop-Client überdeckt — darum jetzt
 *    auch beim Klingeln mittig.
 *  - Am Kopf (callw-head) per Maus/Touch über den GANZEN Viewport VERSCHIEBBAR
 *    (offset-basiert, mit Klick-Schwelle → reiner Klick ändert die Position
 *    nicht). Die gezogene Position bleibt während des Anrufs erhalten (auch beim
 *    Übergang ringing→active, kein Reset bei Re-Render); erst ein neuer Anruf
 *    startet wieder in der Mitte.
 * Zustände im Detail:
 *  - ringing (inbound): Annehmen (grün) / Ablehnen (rot)
 *  - Notiz (P1, T-13): Das Notizfeld steht in jedem Zustand offen (kein
 *    Knopf mehr) — hier wird während des Telefonats notiert, nach dem Auflegen
 *    gibt es kein Notizfeld mehr. Der Entwurf lebt je callId im
 *    TelephonyContext und wird beim Auflegen direkt an den Anruf gehängt
 *    (`appendNotes`). Den Fokus nimmt das Feld nur, wenn niemand anderswo tippt.
 *  - dialing/active: Timer + Stumm/Halten/Beenden, ausklappbar DTMF + Weiterleiten
 *    (Kollegen-Suche per Name/Kürzel oder direkte Nummer, s. KollegenWeiterleitung);
 *    zusätzlich FO/BO-Verantwortlichkeit (P5), wenn der Anruf einem Kontakt zugeordnet ist.
 *  - am Tischtelefon (ADR 0001, Nachtrag): Wurde am Tischtelefon oder in der
 *    Linkus-App abgenommen, zeigt die Karte das Gespräch weiter — Anrufer,
 *    Timer ab Annahme, FO/BO, Kundenakte, Notiz — mit dem Hinweis „Gespräch
 *    läuft am Tischtelefon". Stumm/Halten/Beenden/DTMF/Weiterleiten fehlen
 *    bewusst: das lässt sich vom Browser aus nicht steuern.
 * KEIN backdrop-filter/Blur. Sichtbar nur, wenn ein Anruf läuft UND nicht
 * minimiert ist (minimiert übernimmt die ActiveCallBar oben).
 */

const DTMF_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

const initialsOf = (name?: string | null): string | null => {
    if (!name) return null;
    const parts = name.split(/[\s,]+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
};

const ico = (d: React.ReactNode, size = 18): React.ReactElement => (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" stroke="currentColor"
        strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
        {d}
    </svg>
);
const MinimizeIcon = ico(<path d="M5 13h10" />);
const HangupIcon = ico(
    <><path d="M3.2 8.5c4-2.8 9.6-2.8 13.6 0l-1.1 2.3-2.7-.4-.5-1.7a7.6 7.6 0 00-4.2 0l-.5 1.7-2.7.4z" />
        <path d="M14.5 12.5l2.5 3M17 12.5l-2.5 3" /></>,
);
const MicIcon = ico(<><rect x="7.5" y="2.5" width="5" height="9" rx="2.5" /><path d="M5 9.5a5 5 0 0010 0M10 14.5v3M7.5 17.5h5" /></>);
const MicOffIcon = ico(<><path d="M7.5 4.2A2.5 2.5 0 0112.5 5v3M12.5 9.6a2.5 2.5 0 01-3.6 1.9" /><path d="M5 9.5a5 5 0 007.5 4.3M14.8 9.5a5 5 0 01-1 3M10 16v1.5M7.5 17.5h5M3.5 3.5l13 13" /></>);
const HoldIcon = ico(<><path d="M7 4.5v11M13 4.5v11" /></>);
const PlayIcon = ico(<path d="M6.5 4.5l9 5.5-9 5.5z" />);
const KeypadIcon = ico(<><circle cx="6" cy="6" r="1.1" /><circle cx="10" cy="6" r="1.1" /><circle cx="14" cy="6" r="1.1" /><circle cx="6" cy="10" r="1.1" /><circle cx="10" cy="10" r="1.1" /><circle cx="14" cy="10" r="1.1" /><circle cx="6" cy="14" r="1.1" /><circle cx="10" cy="14" r="1.1" /><circle cx="14" cy="14" r="1.1" /></>);
// Headset mit Mikrofon-Bügel: Geräteauswahl im Gespräch (12.10.2026).
const GeraeteIcon = ico(<><path d="M4 11V9.5a6 6 0 0112 0V11" /><rect x="3" y="11" width="3" height="4.5" rx="1.2" /><rect x="14" y="11" width="3" height="4.5" rx="1.2" /><path d="M15.5 15.5c0 1.3-1.6 2-4 2" /></>);
const TransferIcon = ico(<><path d="M3 7h9M9 4l3 3-3 3" /><path d="M17 13H8M11 16l-3-3 3-3" /></>);

// Bewegungs-Schwelle (px), ab der ein Pointer-Down als Drag (statt reiner Klick)
// zählt. Darunter bleibt die Position unverändert (zentriert bzw. zuletzt gesetzt).
const DRAG_THRESHOLD = 3;

// Beim Ziehen mindestens so viele px der Karte (oben, inkl. Kopf/Steuerung) sichtbar
// lassen — erlaubt es, auch eine HOHE Karte über den ganzen Viewport zu bewegen,
// statt sie oben festzuhalten (sonst müsste die ganze Karte hineinpassen → maxY winzig).
const DRAG_KEEP_VISIBLE = 140;

const CallWidget: React.FC = () => {
    const {
        activeCall,
        owners,
        minimized,
        answer,
        hangup,
        toggleMute,
        toggleHold,
        sendDtmf,
        blindTransfer,
        attendedTransfer,
        rueckfrage,
        rueckfrageVerbinden,
        rueckfrageAbbrechen,
        minimize,
        notizFuer,
        setNotiz,
        tischtelefonSchliessen,
    } = useTelephony();
    const navigate = useNavigate();

    const [showKeypad, setShowKeypad] = useState(false);
    const [showGeraete, setShowGeraete] = useState(false);
    const [showTransfer, setShowTransfer] = useState(false);

    // ── „Kein Ton?"-Hinweis (T-28) ──
    // Liegt KEIN_TON_NACH_MS nach der Annahme noch immer kein Ton an (kein
    // Live-Remote-Audio-Track, <audio> pausiert/stumm oder play() blockiert),
    // zeigt das Fenster einen Hinweis. Sekündlich neu bewertet: Der Hinweis
    // verschwindet, sobald Ton da ist oder der Anruf endet. Das Loggen für die
    // Telefonie-Spuren übernimmt linkusSdk (planeKeinTonPruefung) — auch dann,
    // wenn das Fenster minimiert ist.
    const sdkCall = useSoftphone().activeCall;
    const sdkCallId = sdkCall?.id ?? null;
    const sdkAnsweredAt = sdkCall?.answeredAt ?? null;
    const [keinTon, setKeinTon] = useState(false);
    useEffect(() => {
        setKeinTon(false);
        if (!sdkCallId || sdkAnsweredAt === null) return;
        const pruefe = () => {
            const c = softphone.getState().activeCall;
            if (!c || c.id !== sdkCallId) {
                setKeinTon(false);
                return;
            }
            setKeinTon(
                keinTonNachAnnahme(softphone.audioZustand(), {
                    answeredAt: sdkAnsweredAt,
                    jetzt: Date.now(),
                    gehalten: c.isHold,
                }),
            );
        };
        const erst = window.setTimeout(pruefe, Math.max(0, sdkAnsweredAt + KEIN_TON_NACH_MS - Date.now()));
        const takt = window.setInterval(pruefe, 1000);
        return () => {
            window.clearTimeout(erst);
            window.clearInterval(takt);
        };
    }, [sdkCallId, sdkAnsweredAt]);

    const elapsed = useElapsed(activeCall ? activeCall.startedAt : null);

    // ── Positionierung / Drag (SP7 + P5) ──
    const rootRef = useRef<HTMLDivElement | null>(null);
    // Benutzerdefinierte Position (linke obere Ecke); null = Default (zentriert).
    const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
    // Drag-Bezug: Maus-Startpunkt (px/py) + Karten-Ecke beim Start (ox/oy) +
    // `moved` (Schwelle überschritten?). Offset-basiert → kein Sprung beim Start.
    const dragRef = useRef<{ px: number; py: number; ox: number; oy: number; moved: boolean } | null>(
        null,
    );

    const callId = activeCall?.callId;

    // Neuer Anruf (oder Anruf-Ende) → gezogene Position verwerfen, damit das
    // Widget wieder in der Mitte startet. Keyed auf die callId-
    // Identität (stabil pro Anruf) → kein Reset bei jedem Re-Render. dragRef MUSS
    // mit zurückgesetzt werden: legt die Gegenseite mitten im Drag auf, unmountet
    // der callw-head, bevor pointerup/cancel ihn erreicht → endDrag läuft nie →
    // dragRef.current bliebe mit moved:true stehen und ließe das Widget beim
    // nächsten Anruf schon bei reinem Hover springen.
    useEffect(() => {
        setDragPos(null);
        dragRef.current = null;
    }, [callId]);

    // Fokus statt autoFocus: Sobald das Notizfeld erscheint (Anruf beginnt,
    // Annehmen, Wiederherstellen nach Minimieren), nimmt es den Fokus — außer
    // der Nutzer tippt gerade außerhalb des Widgets in einem Eingabefeld (z. B.
    // Anruf kommt rein, Annahme am Headset). Stabiler Callback-Ref → läuft nur
    // beim Mount, nicht bei jedem Render.
    const notizFokusRef = useCallback((el: HTMLTextAreaElement | null) => {
        if (!el) return;
        const aktiv = aktivesDokument().activeElement as HTMLElement | null;
        const innerhalb = !!aktiv && !!rootRef.current?.contains(aktiv);
        if (darfNotizFokusNehmen(aktiv, innerhalb)) el.focus();
    }, []);

    // Ausgeklappte Bereiche/Eingaben zurücksetzen, sobald der Anruf endet.
    useEffect(() => {
        if (!activeCall) {
            setShowKeypad(false);
            setShowTransfer(false);
            setShowGeraete(false);
        }
    }, [activeCall]);

    // Drag am Kopf (callw-head), in jedem Zustand (auch beim Klingeln). Pointer-
    // Events decken Maus + Touch ab; Pointer-Capture leitet move/up an den Kopf,
    // sodass der Drag nicht abreißt. Beim Down wird NUR der Bezug gemerkt, NICHT
    // die Position gesetzt — ein reiner Klick (Bewegung < Schwelle) lässt die
    // Position daher unverändert (bleibt zentriert). Offset-basiert (Maus minus
    // Karten-Ecke) → kein translate(-50%)-Sprung. Geclamped an den ganzen Viewport.
    const onHeadPointerDown = useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            if (e.pointerType === "mouse" && e.button !== 0) return;
            const el = rootRef.current;
            if (!el) return;
            const r = el.getBoundingClientRect();
            dragRef.current = { px: e.clientX, py: e.clientY, ox: r.left, oy: r.top, moved: false };
            e.currentTarget.setPointerCapture?.(e.pointerId);
        },
        [],
    );
    const onHeadPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        // pointermove feuert auch bei reinem Hover (ohne gedrückten Button). Ein
        // verwaister dragRef (z. B. Unmount-mid-Drag, s. callId-Effekt) würde sonst
        // beim bloßen Überfahren ein Springen auslösen → Hover kann nie ein Drag
        // sein, also Ref verwerfen und raus.
        if (e.buttons === 0) {
            dragRef.current = null;
            return;
        }
        const d = dragRef.current;
        if (!d) return;
        const dx = e.clientX - d.px;
        const dy = e.clientY - d.py;
        // Erst ab Überschreiten der Schwelle als Drag werten (sonst reiner Klick).
        if (!d.moved) {
            if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
            d.moved = true;
        }
        e.preventDefault();
        const el = rootRef.current;
        const w = el?.offsetWidth ?? 340;
        const h = el?.offsetHeight ?? 220;
        // Passt die Karte komplett in den Viewport → ganze Karte sichtbar halten
        // (keine Steuer-Buttons unten raus). NUR wenn sie nicht passt (kurzer Viewport,
        // hohe Karte) die Untergrenze auf den Kopf lockern → dann lässt sie sich
        // überhaupt nach unten bewegen statt oben festzukleben (Issue #2, ohne Regression).
        const keepY = window.innerHeight >= h + 16 ? h : Math.min(h, DRAG_KEEP_VISIBLE);
        const maxX = Math.max(8, window.innerWidth - w - 8);
        const maxY = Math.max(8, window.innerHeight - keepY - 8);
        const x = Math.min(Math.max(d.ox + dx, 8), maxX);
        const y = Math.min(Math.max(d.oy + dy, 8), maxY);
        setDragPos({ x, y });
    }, []);
    const endDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (!dragRef.current) return;
        dragRef.current = null;
        e.currentTarget.releasePointerCapture?.(e.pointerId);
    }, []);

    // Bei Fenster-Resize die gezogene Position re-clampen, damit das Widget nicht
    // aus dem sichtbaren Bereich rutscht.
    const hasCustomPos = dragPos !== null;
    useEffect(() => {
        if (!hasCustomPos) return;
        const onResize = () => {
            const el = rootRef.current;
            const w = el?.offsetWidth ?? 340;
            const h = el?.offsetHeight ?? 220;
            setDragPos((p) =>
                p
                    ? {
                          x: Math.min(Math.max(p.x, 8), Math.max(8, window.innerWidth - w - 8)),
                          y: Math.min(
                              Math.max(p.y, 8),
                              Math.max(
                                  8,
                                  window.innerHeight -
                                      (window.innerHeight >= h + 16 ? h : Math.min(h, DRAG_KEEP_VISIBLE)) -
                                      8,
                              ),
                          ),
                      }
                    : p,
            );
        };
        alleFenster.addEventListener("resize", onResize);
        return () => alleFenster.removeEventListener("resize", onResize);
    }, [hasCustomPos]);

    if (!activeCall || minimized) return null;

    // Effektive Position als Inline-Style (höchste Spezifität → robust), in
    // jedem Zustand gleich (auch beim Klingeln):
    //  - gezogen → explizite px-Koordinaten
    //  - (noch) nicht gezogen → Viewport-Mitte
    const rootStyle: React.CSSProperties = dragPos
        ? {
              position: "fixed",
              left: dragPos.x,
              top: dragPos.y,
              right: "auto",
              bottom: "auto",
              transform: "none",
          }
        : {
              position: "fixed",
              left: "50%",
              top: "50%",
              right: "auto",
              bottom: "auto",
              transform: "translate(-50%, -50%)",
          };

    // Anzeigename: KMVS-Kontakt vor dem Telefonbuch der Anlage (s. anrufAnzeigeName).
    const namensQuelle = {
        number: activeCall.number,
        pbxName: activeCall.name,
        kontaktName: owners?.displayName ?? null,
    };
    const displayName = anrufAnzeigeName(namensQuelle);
    const subNumber = anrufAnzeigeNummer(namensQuelle);
    // Initialen nur aus einem echten Namen — eine Nummer ergäbe „+4" o. Ä.
    const initials = initialsOf(displayName === activeCall.number ? null : displayName);
    const isRinging = activeCall.state === "ringing";
    const amTisch = activeCall.amTischtelefon;
    const kontaktId = owners?.contactId ?? activeCall.contactId;
    // BO-Zeile nur, wenn ein Backoffice zugeordnet ist (boType ≠ NONE).
    const showBo = !!owners && owners.boType !== "NONE";
    const boText = owners
        ? `${owners.boName || "—"}${owners.boType === "TEAM" ? " (Team)" : ""}`
        : "";

    const stateLabel = amTisch
        ? activeCall.direction === "outbound"
            ? "Ausgehendes Gespräch"
            : "Eingehendes Gespräch"
        : activeCall.state === "ringing"
            ? "Eingehender Anruf"
            : activeCall.state === "dialing"
              ? "Wird verbunden …"
              : activeCall.isHold
                ? "Gehalten"
                : "Im Gespräch";

    // In der Rückfrage gehört die Notiz weiter zum gehaltenen Anrufer — sonst
    // stünde im Fenster des Kollegen-Gesprächs ein leeres Feld (08.10.2026).
    const notizCallId = rueckfrage?.anruferCallId ?? activeCall.callId;
    const notiz = notizFuer(notizCallId);
    const notizFeld = (
        <div className="callw-notiz">
            <textarea
                className="callw-notiz-text"
                value={notiz}
                onChange={(e) => setNotiz(notizCallId, e.target.value)}
                placeholder="Notiz zum Gespräch …"
                aria-label="Notiz zum Gespräch"
                rows={4}
                ref={notizFokusRef}
            />
            <div className="callw-thint">Wird nach dem Auflegen am Anruf gespeichert.</div>
        </div>
    );

    const doTransfer = (ziel: string, attended: boolean) => {
        const n = ziel.trim();
        if (!n) return;
        if (attended) attendedTransfer(n);
        else blindTransfer(n);
        setShowTransfer(false);
    };

    return createPortal(
        <div
            ref={rootRef}
            className="kvp-rd callw-root"
            style={rootStyle}
            role="dialog"
            aria-label={isRinging ? "Eingehender Anruf" : amTisch ? "Gespräch am Tischtelefon" : "Aktiver Anruf"}
        >
            <div className="callw-card">
                <button
                    className="callw-min"
                    onClick={minimize}
                    aria-label="Minimieren"
                    title="Minimieren"
                    type="button"
                >
                    {MinimizeIcon}
                </button>

                <div
                    className="callw-head"
                    onPointerDown={onHeadPointerDown}
                    onPointerMove={onHeadPointerMove}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    style={{ cursor: "move", touchAction: "none" }}
                    title="Zum Verschieben hier ziehen"
                >
                    <div className="callw-avatar" aria-hidden>
                        {initials ?? I.phone}
                    </div>
                    <div className="callw-id">
                        <div className="callw-name" title={displayName}>{displayName}</div>
                        {subNumber && <div className="callw-sub">{subNumber}</div>}
                    </div>
                </div>

                <div className="callw-rule" />

                <div className="callw-body">
                    <div className="callw-state">
                        {isRinging || activeCall.state === "dialing" ? (
                            <span className="callw-pill live"><i className="callw-dot" />{stateLabel}</span>
                        ) : (
                            <>
                                <span className="callw-timer">{fmtMMSS(elapsed)}</span>
                                <span className="callw-statelabel">{stateLabel}</span>
                            </>
                        )}
                    </div>

                    {/* FO/BO-Verantwortlichkeit (P5) — nur nach dem Klingeln
                        (aktiv/dialing) und nur, wenn der Anruf einem sichtbaren
                        Kontakt zugeordnet ist. */}
                    {!isRinging && owners && (
                        <div className="callw-owners">
                            <div className="callw-owner">
                                <span className="callw-owner-k">FO</span>
                                <span className="callw-owner-v" title={owners.foName ?? undefined}>
                                    {owners.foName || "—"}
                                </span>
                            </div>
                            {showBo && (
                                <div className="callw-owner">
                                    <span className="callw-owner-k">BO</span>
                                    <span className="callw-owner-v" title={boText}>
                                        {boText}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {amTisch ? (
                        <>
                            <div className="callw-tisch" role="status">
                                <strong>Gespräch läuft am Tischtelefon</strong>
                                <p>
                                    Angenommen am Tischtelefon oder in der Linkus-App. Stumm, Halten,
                                    Weiterleiten und Auflegen bitte dort.
                                </p>
                            </div>

                            {notizFeld}

                            <div className="callw-tools">
                                {kontaktId != null && (
                                    <button
                                        className="callw-tool"
                                        onClick={() => {
                                            minimize();
                                            navigate(`/contacts/${kontaktId}`);
                                        }}
                                        type="button"
                                    >
                                        {PersonIcon} Kundenakte öffnen
                                    </button>
                                )}
                                <button
                                    className="callw-tool"
                                    onClick={tischtelefonSchliessen}
                                    type="button"
                                    title="Nur nötig, wenn das Fenster nach dem Auflegen stehen bleibt"
                                >
                                    Gespräch beendet
                                </button>
                            </div>
                        </>
                    ) : isRinging ? (
                        <>
                            <div className="callw-ring-actions">
                                <button className="callw-btn ok" onClick={answer} type="button">
                                    {I.phone} Annehmen
                                </button>
                                <button className="callw-btn danger" onClick={hangup} type="button">
                                    {HangupIcon} Ablehnen
                                </button>
                            </div>
                            {notizFeld}
                        </>
                    ) : (
                        <>
                            {rueckfrage && (
                                <div className="callw-rueckfrage" role="status">
                                    <strong>
                                        Rückfrage bei {rueckfrage.ziel} — Anrufer
                                        {rueckfrage.anruferNummer ? ` ${rueckfrage.anruferNummer}` : ""} wartet
                                    </strong>
                                    <p>
                                        {rueckfrage.verbindetSeit != null
                                            ? "Wird verbunden …"
                                            : "Der Anrufer hört die Warteschleife. Möchte der Kollege übernehmen, „Verbinden“ — sonst zurück zum Anrufer."}
                                    </p>
                                    {rueckfrage.fehler && <p className="callw-rueckfrage-fehler">{rueckfrage.fehler}</p>}
                                    <div className="callw-rueckfrage-btns">
                                        <button
                                            className="callw-btn ok"
                                            onClick={rueckfrageVerbinden}
                                            type="button"
                                            disabled={!rueckfrage.rueckfrageCallId || rueckfrage.verbindetSeit != null}
                                        >
                                            {TransferIcon} Verbinden
                                        </button>
                                        <button
                                            className="callw-btn"
                                            onClick={rueckfrageAbbrechen}
                                            type="button"
                                            disabled={rueckfrage.verbindetSeit != null}
                                        >
                                            Zurück zum Anrufer
                                        </button>
                                    </div>
                                </div>
                            )}
                            <div className="callw-actions">
                                <button
                                    className={`callw-btn${activeCall.isMute ? " on" : ""}`}
                                    onClick={toggleMute}
                                    type="button"
                                    aria-pressed={activeCall.isMute}
                                >
                                    {activeCall.isMute ? MicOffIcon : MicIcon}
                                    {activeCall.isMute ? "Laut" : "Stumm"}
                                </button>
                                <button
                                    className={`callw-btn${activeCall.isHold ? " on" : ""}`}
                                    onClick={toggleHold}
                                    type="button"
                                    aria-pressed={activeCall.isHold}
                                >
                                    {activeCall.isHold ? PlayIcon : HoldIcon}
                                    {activeCall.isHold ? "Weiter" : "Halten"}
                                </button>
                                <button className="callw-btn danger" onClick={hangup} type="button">
                                    {HangupIcon} Beenden
                                </button>
                            </div>

                            {keinTon && (
                                <div className="callw-keinton" role="status">
                                    <strong>Kein Ton? Mikrofon und Lautsprecher prüfen</strong>
                                    <p>
                                        Klicke oben links in der Adresszeile auf das Schloss-Symbol und stelle
                                        „Mikrofon" auf „Zulassen". Prüfe auch, ob Headset oder Lautsprecher
                                        angeschlossen und laut gestellt sind.
                                    </p>
                                    <button
                                        className="callw-tool"
                                        onClick={() => softphone.tonNeuStarten()}
                                        type="button"
                                    >
                                        Ton erneut starten
                                    </button>
                                    <button className="callw-tool" onClick={() => setShowGeraete(true)} type="button">
                                        Geräte wählen
                                    </button>
                                </div>
                            )}

                            {notizFeld}

                            <div className="callw-tools">
                                <button
                                    className={`callw-tool${showKeypad ? " on" : ""}`}
                                    onClick={() => setShowKeypad((v) => !v)}
                                    type="button"
                                    aria-expanded={showKeypad}
                                >
                                    {KeypadIcon} Tastenfeld
                                </button>
                                <button
                                    className={`callw-tool${showGeraete ? " on" : ""}`}
                                    onClick={() => setShowGeraete((v) => !v)}
                                    type="button"
                                    aria-expanded={showGeraete}
                                    title="Mikrofon, Lautsprecher und Headset wählen"
                                >
                                    {GeraeteIcon} Geräte
                                </button>
                                {!rueckfrage && (
                                    <button
                                        className={`callw-tool${showTransfer ? " on" : ""}`}
                                        onClick={() => setShowTransfer((v) => !v)}
                                        type="button"
                                        aria-expanded={showTransfer}
                                    >
                                        {TransferIcon} Weiterleiten
                                    </button>
                                )}
                            </div>

                            {showKeypad && (
                                <div className="callw-keypad" role="group" aria-label="DTMF-Tastenfeld">
                                    {DTMF_KEYS.map((k) => (
                                        <button
                                            key={k}
                                            className="callw-key"
                                            onClick={() => sendDtmf(k)}
                                            type="button"
                                        >
                                            {k}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {showGeraete && (
                                <div className="callw-geraete">
                                    <TelefonGeraeteWahl kompakt />
                                </div>
                            )}

                            {showTransfer && !rueckfrage && <KollegenWeiterleitung onWeiterleiten={doTransfer} />}
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default CallWidget;
